import React, { useEffect, useRef, useState, useMemo } from "react";
import Storage from "../../../../utility/features/helpers/storageHelper";
import { useNavigate } from "react-router";
import Globals from "../../../../utility/constants/appConstants";
import { IsLoggedIn } from "../../../../utility/features/helpers/authHelper";
import { customerServiceNumberFormatted } from '../../../../utility/utils/formatter';

import {
  PrepayExistingPaymentMethodV2,
  PrepayNewPaymentMethodV2,
} from "../../../../utility/apiClients/account/prePayOTPaymentClient";

import SelectPaymentBlock from "../commonBlocks/selectPaymentBlock";
import NoPaymentDue from '../noPaymentDue';
import PaymentBlock from "../commonBlocks/PaymentBlock";
import ViewPaymentMethodsClient from "../../../../utility/apiClients/customer/ViewPaymentMethodsClient";
import customerSummaryClient from "../../../../utility/apiClients/customer/customerSummaryClient";
import { useSelector, useDispatch } from "react-redux";
import styles from "../../../../styles/Payments/PrePayPayment.module.scss";
import {setBypassAccountDetailsCache} from "../../../../reduxStore/myAccount/accountSummarySlice";
import { setCustomerInfo } from "../../../../reduxStore/login/loginSlice";
import Loader from "../../../loader";
import ErrorBlock from '../../../errorMessage';
import SuccessBlock from '../commonBlocks/successPrepay';
import missionStatusClient from '../../../../utility/apiClients/user/missionStatusClient';

const PrePayPayment = () => {
  const dispatch = useDispatch();
  const nav = useNavigate();

  const [error, setErrorMsg] = useState("");
  const [success, setSuccess] = useState(false);
  const [successProps, setSuccessProps] = useState({});
  const [showNoPayment, setShowNoPayment] = useState(null);
  
  const [hideClass, setHideClass] = useState("d-none");
  const [totalPrePayAmount, setTotalPrePayAmount] = useState(0);
  const [hasSelection, setHasSelection] = useState(false);

  const labels = Globals.acc;

  const missionStatusCheck = async () => {
    try {
      const missionStatus = await missionStatusClient();
      return !["yes"].includes(String(missionStatus?.data).trim().toLowerCase());
    } catch (err) {
      console.error("Error checking mission status api:", err);
      return false;
    }
  };

  const customerSummaryInfo = useSelector((state) => state.login.customerInfo);
  const [customerSummary, setCustomerSummary] = useState(customerSummaryInfo);
  const selectedServiceAddress = useSelector((state) => state.accountSummary.selectedServiceAddress);
  const custNumbers = JSON.parse(localStorage.getItem("customerNumbers")) || [];

  const [serviceAddress, customerNumber] = useMemo(() => {
    let custNumber = "";
    let serviceAddr = "";
    if(selectedServiceAddress != null){
      [serviceAddr, custNumber] = selectedServiceAddress.split(" | ");
    } else {
      custNumber = customerSummary?customerSummary[0].CustomerNumber:"";
      serviceAddr = customerSummary?customerSummary[0].ServiceAddresses[0].address:"";
    }
    
    return [serviceAddr, custNumber];
  }, [selectedServiceAddress, customerSummary]);

  const payBlockRef = useRef(null);

  const [isLoading, setIsLoading] = useState({
    isCustomerSummaryApiLoaded : false,
    isSaleAgreementApiLoaded : false,
    isPaymentMethodApiLoaded : false
  });

  const [unauthorizeResponse, setUnauthorizeResponse] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [salesAgreementNumbers, setSalesAgreementNumbers] = useState({
    value: [],
    data: {},
  });

  const CustomerDetailsApiCallRef = useRef(customerNumber);
   
  useEffect(() => {
    if(CustomerDetailsApiCallRef.current != customerNumber) {
      CustomerDetailsApiCallRef.current = customerNumber;
      setCustomerSummary(null);
    }
  }, [customerNumber]);

  useEffect(() => {
    setCustomerSummary(customerSummaryInfo);
  }, [customerSummaryInfo]);

  const getCustomerSummary = async (custNum) => {
    if (custNum === "") return;

    const isMissionAvailable = await missionStatusCheck();
    if (!isMissionAvailable) {
      setErrorMsg(labels.missionDownErrorDisplay);
      setIsLoading((prev) => ({ ...prev, isCustomerSummaryApiLoaded: false, isSaleAgreementApiLoaded: false }));
      return;
    }

    setErrorMsg("");
    setIsLoading((prev) => ({
      ...prev,
      isCustomerSummaryApiLoaded: false,
      isSaleAgreementApiLoaded: false
    }));
    try {
      const customerSummary = await customerSummaryClient(custNum);
      if (customerSummary?.status && customerSummary?.status === 401 ) {
        console.log("Unauthorized access - 401");
        localStorage.clear();
        nav("/login", { replace: true });
        return;
      }
      
      if (
        customerSummary?.data === null ||
        customerSummary?.data === undefined
      ) {
        setErrorMsg("Some error occurred.");
        return;
      }
      dispatch(setCustomerInfo(customerSummary?.data));
    } catch (err) {
      setErrorMsg("Some error occurred.");
    } finally {
      setIsLoading((prev) => ({
        ...prev,
        isCustomerSummaryApiLoaded: true
      }));
    }
  };

  const fetchPaymentData = async (customerNumber) => {
    const isMissionAvailable = await missionStatusCheck();
    if (!isMissionAvailable) {
      setErrorMsg(labels.missionDownErrorDisplay);
      setIsLoading((prev) => ({ ...prev, isPaymentMethodApiLoaded: false }));
      return;
    }

    try {
      
      setIsLoading((prev) => ({
        ...prev,
        isPaymentMethodApiLoaded : false
      }))

      const paymentInfo = await ViewPaymentMethodsClient({
        cusNum: customerNumber,
      });
      if(paymentInfo && paymentInfo.error && paymentInfo.error != ""){
        setPaymentData(null);
        setErrorMsg("Some error occurred.");
      } else {
        if (paymentInfo && !paymentInfo.errorStatus) {
          const methods = paymentInfo?.data?.PaymentMethods || [];

          setPaymentData(methods);

        } else if (paymentInfo?.errorStatus === 401) {
          // Flag unauthorized → handle in effect below
          setUnauthorizeResponse(true);
        } else {
          throw new Error(
            typeof paymentInfo === "object"
              ? JSON.stringify(paymentInfo)
              : String(paymentInfo)
          );
        }
      }
    } catch (error) {
      setErrorMsg("Some error occurred.");
    } finally {
      setHideClass("");
      setIsLoading((prev) => ({
        ...prev,
        isPaymentMethodApiLoaded : true
      }))
    }
  };

  // redirect is unauthorize
  useEffect(() => {
    if (!IsLoggedIn() || unauthorizeResponse) {
      
      (async () => {
        await Storage.logoutUser();
        window.location = "/login";
      })();
    }
  }, [unauthorizeResponse]);

  useEffect(() => {
    if (customerSummary != null) {
      (async () => {
        setIsLoading((prev) => ({
          ...prev,
          isCustomerSummaryApiLoaded: true,
          isSaleAgreementApiLoaded: false
        }));
        setShowNoPayment(null);
        await fetchPaymentData(customerNumber);
      })();
    } else {
      (async () => {
        await getCustomerSummary(customerNumber);
      })();
    }

    return () => {
      setErrorMsg("");
    }
  }, [customerSummary, selectedServiceAddress, serviceAddress, customerNumber]);

  const handleSubmit = async (paymentData, billingData, isNew, authorizedCardDetails) => {
    const isMissionAvailable = await missionStatusCheck();
    if (!isMissionAvailable) {
      setErrorMsg(labels.missionDownErrorDisplay);
      window.scrollTo(0, 0);
      return;
    }

    const salesAgreementObj = salesAgreementNumbers;

    if (isNew) {
      if(paymentData.isCreditCard && authorizedCardDetails.AuthorizedCard === false){
        setErrorMsg("Card authorization failed. Please try different card details.");
        window.scrollTo(0, 0);
      } else {
        PrepayNewPaymentMethodV2(isNew, "prepay", paymentData, customerNumber, salesAgreementObj, authorizedCardDetails).then(
          (response) => {
            
            if(typeof response.error === "undefined"){
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({
                'event' : 'account_action',
                'category': 'Account',
                'action': 'payments',
                'label': `prepay initiated - ${paymentData.isCreditCard ? 'Card' : 'Account'}`,
                'customer_id': (customerNumber || {}),
                'error' : {
                  'reason' : ''
              }})
              
              setSuccessProps({
                  cashSubTyp: paymentData.isCreditCard && !paymentData.isAccount ? paymentData.cashSubType : 'CHK',
                  lastFour: paymentData.isCreditCard && !paymentData.isAccount ? paymentData['Card Number'].slice(-4) : paymentData['Account Number'].slice(-4),
                  salesAgreements: salesAgreementObj,
                  confNum: response
              });
              setSuccess(true);

              dispatch(setBypassAccountDetailsCache(true));
            } else {
              if(payBlockRef.current){
                payBlockRef.current.updateIsLoading(false);
              }
              setErrorMsg(`An error has occurred in processing the payment. Please contact Customer Care at ${customerServiceNumberFormatted}`);
              window.scrollTo(0, 0);
            }
          },
          (error) => {
            if(error?.genericMessage)
              setErrorMsg(error?.genericMessage);
            else
              setErrorMsg(`An error has occurred in processing the payment. Please contact Customer Care at ${customerServiceNumberFormatted}`);
            window.scrollTo(0, 0);
          }
        );
      } 
    }
    else { 
      
        if (paymentData.pccPaymentType !== 'BankAccount') {
          
          PrepayExistingPaymentMethodV2(isNew, "prepay", paymentData, customerNumber, salesAgreementObj, "").then(
            (response) => {
              if(typeof response.error === "undefined"){
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  'event' : 'account_action',
                  'category': 'Account',
                  'action': 'payments',
                  'label': `enrollment initiated - ${paymentData.pccCashType === 'CCD' ? 'Card' : 'Account'}`,
                  'customer_id': (customerNumber || {}),
                  'error' : {
                    'reason' : ''
                  }})

                setSuccessProps({
                  
                    cashSubTyp: paymentData.pccCashSubType,
                    lastFour: paymentData.pccAcctLast4,
                    salesAgreements: salesAgreementObj,
                    confNum: response
                  }
                );

                setSuccess(true);

                dispatch(setBypassAccountDetailsCache(true));
              } else {
                
                if(payBlockRef.current){
                  payBlockRef.current.updateIsLoading(false);
                }
                setErrorMsg(`An error has occurred in processing the payment. Please contact Customer Care at ${customerServiceNumberFormatted}`);
                window.scrollTo(0, 0);
              }
            },
            (error) => {
              setErrorMsg(`An error has occurred in processing the payment. Please contact Customer Care at ${customerServiceNumberFormatted}`);
              window.scrollTo(0, 0);
            }
          );
          
        } else {
          PrepayExistingPaymentMethodV2(isNew, "prepay", paymentData, customerNumber, salesAgreementObj, "").then(
            (response) => {
              if(typeof response.error === "undefined"){
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  'event' : 'account_action',
                  'category': 'Account',
                  'action': 'payments',
                  'label': `enrollment initiated - ${paymentData.pccCashType === 'CCD' ? 'Card' : 'Account'}`,
                  'customer_id': (customerNumber || {}),
                  'error' : {
                    'reason' : ''
                  }});

                  setSuccessProps({
                    cashSubTyp: paymentData.pccCashSubType,
                    lastFour: paymentData.pccAcctLast4,
                    salesAgreements: salesAgreementObj,
                    confNum: response
                  });

                  setSuccess(true);

                  dispatch(setBypassAccountDetailsCache(true));
              } else {
                  if(payBlockRef.current){
                    payBlockRef.current.updateIsLoading(false);
                  }
                  setErrorMsg(`An error has occurred in processing the payment. Please contact Customer Care at ${customerServiceNumberFormatted}`);
                  window.scrollTo(0, 0);
              }
            },
            (error) => {
              setErrorMsg(`An error has occurred in processing the payment. Please contact Customer Care at ${customerServiceNumberFormatted}`);
              window.scrollTo(0, 0);
            }
          );
        }
      
    }
  }
  const setTotalAmntHandler = (value) => {
    const val = value.dollars + "." + value.cents.trim();
    setTotalPrePayAmount(val);
  };

  return (
    <>
      {error != "" && <ErrorBlock labels={labels} errMsg={error} />}
      {success && <SuccessBlock labels={labels} successProps={successProps} />}
      {error == "" && !success && <>
        <div 
        className={`${styles["bg-gray-100"]} ${styles["mb-40"]} ${styles["p-md-30"]} ${styles["mb-40"]} ${styles["tru-mma-main-wrapper"]}`}
        >
          <div className="container-fluid">
           <div className='row'>
                <div className={`col-12 ${styles["gfw-3"]} ${styles["mb-20"]} d-block`}>
                  <span className={`d-block ${styles["gfs-24"]} ${styles["gfs-30"]} ${styles["glh-1_2"]} ${styles["mb-10"]} ${styles["text-gray-med"]}`}>PrePay Annually and Save</span>
                  <span className={`d-block ${styles["gfs-20"]} ${styles["mb-20"]}`} >
                    
                  </span>
                  <span className={`d-block ${styles["gfs-20"]}`}>See how much you&apos;ll save by selecting services below. For more details, see <a href={`${process.env.REACT_APP_RESI_URL}/about/terms`} rel="noopener noreferrer" target="_blank" className={`${styles['aTag']}`}>Terms and Conditions</a>.</span>
                </div>
              </div>
          {(!isLoading.isSaleAgreementApiLoaded || !isLoading.isPaymentMethodApiLoaded) && <Loader />}
          {showNoPayment && isLoading.isSaleAgreementApiLoaded && isLoading.isPaymentMethodApiLoaded && <NoPaymentDue labels={labels} />}
          {!showNoPayment && isLoading.isCustomerSummaryApiLoaded &&
            <form className={`ShowLoadingModal ${(!isLoading.isSaleAgreementApiLoaded || !isLoading.isPaymentMethodApiLoaded)?'d-none':''}`} id='PaymentForm' method='post'>
              <div className={styles.selectPaymentBlock}>
                <SelectPaymentBlock  labels={labels} salesAgreementNumbers={salesAgreementNumbers} setSalesAgreementNumbers={setSalesAgreementNumbers} showNoPayment={setShowNoPayment} setHideClass={setHideClass} totalAmount={setTotalAmntHandler} setHasSelection={setHasSelection} setParentLoading={setIsLoading} isParentLoading={isLoading} />
              </div>

              {hasSelection &&
              <div className={styles.paymentBlock}>
                <PaymentBlock ref={payBlockRef} salesAgreementNumbers={salesAgreementNumbers} labels={labels} onSubmit={handleSubmit} setHideClass={setHideClass} totalAmountPrePay={totalPrePayAmount} paymentDataProp={paymentData} isParentLoading={isLoading} />
              </div>
              }
            </form>
          }
        </div>
        </div>
      </>}
      
    </>
  );
};

export default PrePayPayment;
